import React, { useRef, useCallback, useEffect, useState } from 'react';
import { ArrowUp, X, Spiral, Plugs, ArrowRight } from '@phosphor-icons/react';
import { AnyWindowEntity } from '../../../types/windows';
import { getWindowTitle } from '../utils/windowUtils';
import useSpaceStore from '../../../stores/spaceStore';
import { useEntityIcon } from '../../../hooks/useEntityIcon';
import WaterDroplet from '../layouts/WaterDroplet';
import type { AmbientActionCard } from '../../../hooks/useAmbientSuggestions';
import './ContextualAIModal.scss';
import { entityRegistry } from '../../../stores/entityRegistry';

export type ContextSource = 
  | { type: 'windows'; windowIds: string[] }
  | { type: 'entities'; entityIds: string[] }
  | { type: 'screenArea'; bounds: { x: number; y: number; width: number; height: number } }
  | { type: 'text'; text: string; source?: string };

export interface AmbientMessageDisplay {
  messageText: string;
  cards: AmbientActionCard[];
}

interface ContextualAIModalProps {
  isOpen: boolean;
  onClose: () => void;
  context: ContextSource;
  onSubmit: (prompt: string, context: ContextSource) => void;
  isProcessing?: boolean;
  position?: { x: number; y: number };
  ambientMessage?: AmbientMessageDisplay;
  initialPrompt?: string;
}

export const ContextualAIModal: React.FC<ContextualAIModalProps> = ({
  isOpen,
  onClose,
  context,
  onSubmit,
  isProcessing = false,
  position,
  ambientMessage,
  initialPrompt,
}) => {
  const [prompt, setPrompt] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [modalPosition, setModalPosition] = useState(position || { x: 0, y: 0 });
  
  // Use the proper entity icon hook
  const { getEntityIcon } = useEntityIcon({ size: 12 });

  // Center the modal on mount or update position
  useEffect(() => {
    if (isOpen && !position) {
      const centerX = window.innerWidth / 2 - 250; // 250 = half of modal width (500px)
      const centerY = window.innerHeight / 2 - 150; // Approximate half height
      setModalPosition({ x: Math.max(20, centerX), y: Math.max(20, centerY) });
    } else if (position) {
      setModalPosition(position);
    }
  }, [isOpen, position]);

  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  }, []);

  useEffect(() => {
    adjustTextareaHeight();
  }, [prompt, adjustTextareaHeight]);

  useEffect(() => {
    if (isOpen && textareaRef.current) {
      const timer = setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Seed the textarea with initialPrompt whenever the modal opens
  useEffect(() => {
    if (isOpen) {
      setPrompt(initialPrompt ?? '');
      setTimeout(adjustTextareaHeight, 0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Dragging handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.contextual-ai-modal__close-button')) {
      return;
    }
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - modalPosition.x,
      y: e.clientY - modalPosition.y,
    });
  }, [modalPosition]);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      setModalPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y,
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !isProcessing) {
      e.preventDefault();
      handleSubmit();
    }
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleSubmit = useCallback(() => {
    if (prompt.trim() && !isProcessing) {
      onSubmit(prompt, context);
      setPrompt('');
    }
  }, [prompt, isProcessing, onSubmit, context]);

  const renderContextChips = () => {
    switch (context.type) {
      case 'windows': {
        // No windows selected — render nothing (modal acts as a plain AI prompt)
        if (context.windowIds.length === 0) return null;

        const windows = useSpaceStore.getState().windows;
        const windowData = context.windowIds
          .map(id => {
            const window = windows[id];
            const entity = window ? useSpaceStore.getState().getWindowEntity(window.id) : null;
            return window && entity ? { id, window, entity, title: getWindowTitle(window) } : null;
          })
          .filter((item): item is { id: string; window: AnyWindowEntity; entity: any; title: string } => item !== null);

        return (
          <div className="contextual-ai-modal__context-chips">
            {windowData.map(({ id, entity, title }) => (
              <span
                key={id}
                className="entity-pill"
                title={title}
              >
                <span className="entity-pill__icon">{getEntityIcon(entity)}</span>
                {title}
              </span>
            ))}
          </div>
        );
      }
      case 'entities': {
        // Fetch entity info from the registry to show entity pills
        const entities = (entityRegistry.dumpAll().flatMap((__s) => __s.entities) as any[]);
        const entityData = context.entityIds
          .map(id => {
            const entity = entities.find(e => e._id === id);
            return entity ? { id, entity, name: entity.name, type: entity.entityType } : null;
          })
          .filter((item): item is { id: string; entity: any; name: string; type: string } => item !== null);

        return (
          <div className="contextual-ai-modal__context-chips">
            {entityData.map(({ id, entity, name, type }) => (
              <span
                key={id}
                className="entity-pill"
                title={`${type}: ${name}`}
              >
                <span className="entity-pill__icon">{getEntityIcon(entity)}</span>
                {name}
              </span>
            ))}
          </div>
        );
      }
      case 'screenArea': {
        return (
          <div className="contextual-ai-modal__context-label">
            Screen area selected
          </div>
        );
      }
      case 'text': {
        return (
          <div className="contextual-ai-modal__context-label">
            Text selected{context.source ? ` from ${context.source}` : ''}
          </div>
        );
      }
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={modalRef}
      className={`contextual-ai-modal ${isDragging ? 'dragging' : ''}`}
      style={{
        position: 'fixed',
        left: `${modalPosition.x}px`,
        top: `${modalPosition.y}px`,
      }}
    >
      <div className="contextual-ai-modal__content">
        <div className="contextual-ai-modal__header" onMouseDown={handleMouseDown}>
          <div className="contextual-ai-modal__header-content">
            {renderContextChips()}
          </div>
          <button
            className="contextual-ai-modal__close-button"
            onClick={onClose}
            title="Close (Esc)"
          >
            <X size={16} />
          </button>
        </div>

        {ambientMessage && (
          <div className="contextual-ai-modal__ambient-message">
            <div className="contextual-ai-modal__ambient-avatar">
              <WaterDroplet size={16} />
              <span className="contextual-ai-modal__ambient-name">River</span>
            </div>
            <p className="contextual-ai-modal__ambient-text">{ambientMessage.messageText}</p>
            {ambientMessage.cards.length > 0 && (
              <div className="contextual-ai-modal__ambient-cards">
                {ambientMessage.cards.map((card) => (
                  <div key={card.id} className="contextual-ai-modal__ambient-card">
                    <Plugs size={13} weight="duotone" className="contextual-ai-modal__ambient-card-icon" />
                    <span className="contextual-ai-modal__ambient-card-title">{card.title}</span>
                    {card.subtitle && (
                      <span className="contextual-ai-modal__ambient-card-sub">{card.subtitle}</span>
                    )}
                    <button
                      className="contextual-ai-modal__ambient-card-btn"
                      onClick={() => {
                        onSubmit(card.connectPrompt, context);
                      }}
                    >
                      Connect <ArrowRight size={11} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="contextual-ai-modal__ambient-divider" />
          </div>
        )}

        <div className="contextual-ai-modal__input-row">
          <div className="contextual-ai-modal__input-container">
            <textarea
              ref={textareaRef}
              className="contextual-ai-modal__input"
              placeholder={
                context.type === 'windows' && context.windowIds.length === 0
                  ? 'Ask AI anything…'
                  : 'Ask anything about this selection…'
              }
              value={prompt}
              onChange={(e) => {
                setPrompt(e.target.value);
                adjustTextareaHeight();
              }}
              disabled={isProcessing}
              autoFocus
              onKeyDown={handleKeyDown}
              rows={1}
            />
          </div>
        </div>

        <div className="contextual-ai-modal__button-row">
          <div className="contextual-ai-modal__button-row-left" />
          <div className="contextual-ai-modal__button-row-right">
            <button
              className="contextual-ai-modal__send-button"
              onClick={handleSubmit}
              disabled={!prompt.trim() || isProcessing}
              title={isProcessing ? 'Processing...' : 'Send (Enter)'}
            >
              {isProcessing ? (
                <Spiral size={16} weight="bold" className="contextual-ai-modal__processing-indicator" />
              ) : (
                <ArrowUp size={16} />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

